import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { SwipeableTemporaryDrawer } from "../components/SidebarMenu";
import DropDown from "./DropDows";
import NotificationsIcon from "@mui/icons-material/Notifications";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Badge from "@mui/material/Badge";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth } from "../components/AuthContext/AuthContext";
import { useNotificationContext } from "../components/AuthContext/NotificationContext";
import Fade from "@mui/material/Fade";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { DRAWER_WIDTH_COMPACT } from "./SidebarMenu/constants";

const Nav = ({ showButton }) => {
  const LogoIco = "/img/logo.webp";
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const pointLike = "/img/ponty.png";
  
  const [marqueeText, setMarqueeText] = useState(null);

  useEffect(() => {
    const fetchMarqueeText = async () => {
      try {
        // En caso de que termine o no en slash
        const baseUrl = process.env.REACT_APP_BASE_URL?.endsWith('/') 
          ? process.env.REACT_APP_BASE_URL 
          : `${process.env.REACT_APP_BASE_URL}/`;
          
        const response = await fetch(`${baseUrl}mensaje-nav/activo`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.mensaje) {
            setMarqueeText(data.mensaje);
          }
        }
      } catch (error) {
        console.error("Error fetching marquee text:", error);
      }
    };
    fetchMarqueeText();
  }, []);



  const { getSocket, isConnected, userData } = useAuth();


  const { notifications, addNotification, clearNotifications } = useNotificationContext();


  const [showToast, setShowToast] = useState(false);

  // Socket listener — se ejecuta cuando isConnected cambia (conecta/desconecta).
  // getSocket() no está en deps: es un getter estable que lee socketRef.current,
  // incluirlo causaría que el efecto disparara por cambios de referencia de función
  // (por el useMemo del contexto) sin que el socket haya cambiado realmente.
  useEffect(() => {
    const socket = getSocket();

    if (!socket) return; // socket aún no disponible; se reintentará cuando isConnected cambie



    // 🆕 Listener para notificaciones de pagos
    const handleNotificationPago = (data) => {
      const { tipo = 'info', mensaje = '📩 Tienes una nueva notificación', solicitudId } = data;


      // Emoji según el tipo
      const emoji = {
        success: '💰✅',
        error: '❌',
        warning: '⚠️',
        info: 'ℹ️'
      }[tipo] || '💰';

      addNotification({
        message: `${emoji} ${mensaje}`,
        type: tipo,
        data: solicitudId,
      });

      setShowToast(true);
    };

    // ✅ Listener existente (solicitud-web-usuario)
    const handleNotification = (data) => {
      const mensaje = data?.mensaje || "📩 Tienes una nueva notificación";


      addNotification({
        message: mensaje,
        type: 'info',
      });

      setShowToast(true);
    };

    // Verificar si los listeners ya están registrados
    const isAlreadyListening = socket.hasListeners("solicitud-web-usuario");

    if (!isAlreadyListening) {
      // Registrar los listeners SOLO si no existen
      socket.on("solicitud-web-usuario", handleNotification);
      socket.on("nueva-notificacion-pago", handleNotificationPago);
    }

    // Cleanup: NO remover listeners para mantenerlos activos entre cambios de ruta
    return () => {
      // Intencional: los listeners se mantienen vivos entre navegaciones de ruta.
    };
  }, [isConnected]); // eslint-disable-line react-hooks/exhaustive-deps


  // Ocultar toast automáticamente
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => setShowToast(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Las notificaciones actuales no tienen sistema de "leído"
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (id, event) => {
    event.stopPropagation();
    // removeNotification(id); // Usar función del contexto si existe
  };

  const handleClearAll = () => {
    clearNotifications();
    handleClose();
  };

  // Contar notificaciones no leídas
  const unreadCount = 0; // Las nuevas notificaciones no tienen sistema de "leído"

  return (
    <>
      <nav
        className="fixed top-0 left-0 z-50 w-full h-20 bg-morado px-4 lg:px-12 py-3 text-white flex items-center justify-between shadow-md border-b border-white/10"
      >
        <div className="flex items-center gap-3">
          {userData && (
            <SwipeableTemporaryDrawer
              userDataToken={userData}
              className="flex-shrink-0 w-full md:w-auto"
            />
          )}
          <Link to="/dashboard" className="ml-1 rounded-lg">
            <img
              className="w-24 lg:w-32 xl:w-36 cursor-pointer transition-transform duration-200 hover:scale-105 drop-shadow-sm"
              src={LogoIco}
              alt="Logo Point"
            />
          </Link>
        </div>

        {marqueeText && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:flex items-center gap-4 pointer-events-none overflow-hidden rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm px-4 py-1.5 shadow-lg">
            {/* Imagen fija */}
            <img
              src={pointLike}
              alt="Point Like"
              className="w-14 h-14 object-contain flex-shrink-0"
              style={{
                filter: "drop-shadow(8px 0px 8px rgba(0,0,0,0.35))"
              }}
            />

            {/* Texto */}
            <div className="marquee-container">
              <div className="marquee-text">
                <span className="text-white font-bold text-xl xl:text-2xl colorCycle flex items-center gap-2">
                  {marqueeText}
                </span>
              </div>
            </div>
          </div>
        )}


        {showButton && (
          <div className="flex items-center gap-3 sm:gap-4 justify-end flex-shrink-0">
            <span className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-2.5 sm:px-3 py-1 text-xs sm:text-sm font-medium truncate max-w-[130px] sm:max-w-[220px]">
              {userData?.Nombre || "Usuario"}
            </span>

            <DropDown />

            {/* Notificaciones */}
            <div>
              <IconButton
                onClick={handleClick}
                className="transition-transform duration-200 hover:scale-110"
                sx={{
                  backgroundColor: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  '&:hover': {
                    backgroundColor: "rgba(255,255,255,0.2)",
                  }
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  color="error"
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.7rem",
                      height: 18,
                      minWidth: 18,
                      top: 4,
                      right: 4,
                    },
                  }}
                >
                  <NotificationsIcon sx={{ color: "white" }} />
                </Badge>
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    width: 350,
                    maxHeight: 400,
                    borderRadius: '10px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                    overflow: 'hidden'
                  }
                }}
                TransitionComponent={Fade}
              >
                <div className="bg-morado text-white py-3 px-4 flex justify-between items-center">
                  <Typography variant="subtitle1" fontWeight="medium">
                    Notificaciones ({notifications.length})
                  </Typography>
                  {notifications.length > 0 && (
                    <button
                      onClick={handleClearAll}
                      className="text-xs text-white/80 hover:text-white hover:underline transition-colors"
                    >
                      Borrar todas
                    </button>
                  )}
                </div>
                <Divider />

                <div className="max-h-72 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="py-6 px-4 text-center text-gray-500">
                      <div className="text-4xl mb-2">🔕</div>
                      <Typography variant="body2">
                        No tienes notificaciones pendientes
                      </Typography>
                    </div>
                  ) : (
                    notifications.map((noti, index) => (
                      <React.Fragment key={noti.id}>
                        <MenuItem
                          className={`py-3 px-4`}
                        >
                          <div className="flex w-full">
                            <div className="flex-grow pr-2">
                              <div className="flex items-start justify-between">
                                <div className="font-medium mb-1 text-sm">
                                  {noti.message}
                                </div>
                                <IconButton
                                  size="small"
                                  className="ml-2 -mt-1 -mr-1"
                                  onClick={(e) => handleDelete(noti.id, e)}
                                >
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                              </div>
                              <div className="text-xs text-gray-500 flex items-center">
                                <span>{new Date(noti.timestamp).toLocaleTimeString("es-ES")}</span>
                              </div>
                            </div>
                          </div>
                        </MenuItem>
                        {index < notifications.length - 1 && <Divider />}
                      </React.Fragment>
                    ))
                  )}
                </div>
              </Menu>
            </div>
          </div>
        )}
      </nav>

      {/* Toast flotante mejorado */}
      {showToast && (
        <div
          className="fixed bottom-4 right-4 bg-white text-gray-800 shadow-xl rounded-xl border-l-4 border-blue-500
                   flex items-center space-x-3 z-50 overflow-hidden transition-all duration-300 
                   animate-slide-in-right max-w-md"
        >
          <div className="py-4 px-5 flex items-center space-x-3">
            <div className="flex-shrink-0 bg-blue-100 rounded-full p-2">
              <NotificationsIcon sx={{ color: "#3b82f6", fontSize: "1.5rem" }} />
            </div>
            <div className="flex-1 pr-8">
              <div className="font-medium text-sm">Nueva notificación</div>
              <div className="text-sm text-gray-600 mt-1">{notifications[notifications.length - 1]?.message || "Tienes un nuevo aviso"}</div>
            </div>
            <button
              onClick={() => setShowToast(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
          <div className="h-1 w-full bg-gray-100 absolute bottom-0 left-0">
            <div
              className="h-full bg-blue-500 animate-shrink"
              style={{
                animation: 'shrink 5s linear forwards',
              }}
            />
          </div>
        </div>
      )}


      {/* Estilos CSS adicionales agregados internamente */}
      <style>
        {`
    /* Contenedor que oculta el exceso */
  .marquee-container {
    position: relative;
    width: 460px;
    overflow: hidden;
    white-space: nowrap;
  }

  /* Animación */
  @keyframes marqueeSlide {
    0% { transform: translateX(8%); }
    100% { transform: translateX(-100%); }
  }

  /* Texto que se mueve */
  .marquee-text {
    display: inline-block;
    animation: marqueeSlide 18s linear infinite;
  }


  .colorCycle {
  animation: cycleColor 6s linear infinite;
}

@keyframes cycleColor {
  0% { color: #ffffff; }
  30% { color: #FF9F45; }
  60% { color: #9DB4FF; }
  100% { color: #ffffff; }
}

.bounce {
  animation: bounceLoop 1s ease-in-out infinite;
}

@keyframes bounceLoop {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
}
  `}
      </style>
      {/* Spacer to prevent layout jumps since nav is fixed */}
      <div className="h-20 w-full flex-shrink-0 pointer-events-none" />
    </>
  );
};

// ✅ Memoizar Nav para evitar re-renders si las props no cambian
export default React.memo(Nav);

